import { defineStore } from "pinia";
import type { MerchantBillingStatusDto } from "@price-rescue/api-contracts";

import { MerchantApiError } from "../authentication/errors.js";
import type { BillingService } from "./service.js";

export const useBillingStore = defineStore("merchant-billing", {
  state: (): {
    billing?: MerchantBillingStatusDto;
    errorMessage?: string;
    loading: boolean;
    service?: BillingService;
  } => ({ loading: false }),
  actions: {
    configure(service: BillingService): void {
      this.service = service;
    },
    async loadStatus(): Promise<void> {
      await this.fetchStatus(
        () => this.requireService().loadStatus(),
        "Billing details could not be loaded.",
      );
    },
    async refreshStatus(): Promise<void> {
      await this.fetchStatus(
        () => this.requireService().refreshStatus(),
        "Billing status could not be refreshed.",
      );
    },
    requireService(): BillingService {
      if (this.service === undefined)
        throw new Error("Billing store was not configured before use.");
      return this.service;
    },
    async fetchStatus(
      fetch: () => Promise<MerchantBillingStatusDto>,
      fallback: string,
    ): Promise<void> {
      this.loading = true;
      delete this.errorMessage;
      try {
        this.billing = await fetch();
      } catch (error: unknown) {
        this.errorMessage = errorMessage(error, fallback);
      } finally {
        this.loading = false;
      }
    },
  },
});

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.kind === "access") {
    return "Only Owners and App Admins can view billing details.";
  }
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  return error instanceof Error ? error.message : fallback;
}
